import React, { useRef, useState, useEffect } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, Link } from 'lucide-react';

export interface RichTextEditorProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    minHeight?: string;
    maxHeight?: string;
    className?: string;
}

export const ensureHtml = (input: string): string => {
    if (!input) return '';

    let html = input;

    // Convert markdown links [text](url) -> <a href="url" target="_blank" rel="noopener noreferrer">text</a>
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-[#0094c8] underline font-medium">$1</a>');

    // Convert markdown bold **text** or __text__ -> <strong>text</strong>
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.*?)__/g, '<strong>$1</strong>');

    // Convert markdown italic *text* -> <em>text</em>
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');

    // If input already contains block HTML elements (<p>, <ul>, <ol>, <div>, <h1..6>), return processed html
    const hasBlockTags = /<(p|ul|ol|li|div|h[1-6])[\s>]/i.test(html);
    if (hasBlockTags) {
        return html;
    }

    // Process line-by-line for bullet/numbered lists and paragraphs
    const lines = html.split('\n');
    let inList = false;
    let listType: 'ul' | 'ol' | null = null;

    const formattedLines = lines.map(line => {
        const trimmed = line.trim();
        if (!trimmed) {
            if (inList) {
                const closed = listType === 'ul' ? '</ul>' : '</ol>';
                inList = false;
                listType = null;
                return closed + '<br />';
            }
            return '<br />';
        }

        // Bullet point detection: • or - or * at start of line
        if (/^[•\-\*]\s+/.test(trimmed)) {
            const content = trimmed.replace(/^[•\-\*]\s+/, '');
            let item = `<li>${content}</li>`;
            if (!inList || listType !== 'ul') {
                const prefix = inList ? (listType === 'ol' ? '</ol><ul>' : '<ul>') : '<ul>';
                inList = true;
                listType = 'ul';
                return prefix + item;
            }
            return item;
        }

        // Numbered list detection: e.g. 1. 2. 3.
        const numMatch = trimmed.match(/^(\d+)\.\s+(.*)/);
        if (numMatch) {
            const content = numMatch[2];
            let item = `<li>${content}</li>`;
            if (!inList || listType !== 'ol') {
                const prefix = inList ? (listType === 'ul' ? '</ul><ol>' : '<ol>') : '<ol>';
                inList = true;
                listType = 'ol';
                return prefix + item;
            }
            return item;
        }

        let closed = '';
        if (inList) {
            closed = listType === 'ul' ? '</ul>' : '</ol>';
            inList = false;
            listType = null;
        }
        return closed + `<p>${trimmed}</p>`;
    });

    if (inList) {
        formattedLines.push(listType === 'ul' ? '</ul>' : '</ol>');
    }

    return formattedLines.filter(Boolean).join('');
};

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
    value,
    onChange,
    placeholder = 'Enter details...',
    minHeight = 'min-h-20',
    maxHeight = 'max-h-52',
    className = ''
}) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const localValueRef = useRef(value);

    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [isBulletList, setIsBulletList] = useState(false);
    const [isOrderedList, setIsOrderedList] = useState(false);
    const [showLinkInput, setShowLinkInput] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const [savedSelection, setSavedSelection] = useState<Range | null>(null);

    const saveSelection = () => {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
            setSavedSelection(sel.getRangeAt(0));
        }
    };

    const restoreSelection = () => {
        if (savedSelection) {
            const sel = window.getSelection();
            if (sel) {
                sel.removeAllRanges();
                sel.addRange(savedSelection);
            }
        }
    };

    const applyLink = () => {
        if (linkUrl.trim()) {
            let formattedUrl = linkUrl.trim();
            if (!/^https?:\/\//i.test(formattedUrl)) {
                formattedUrl = 'https://' + formattedUrl;
            }
            restoreSelection();
            if (editorRef.current) {
                editorRef.current.focus();
                document.execCommand('createLink', false, formattedUrl);
                handleInput();
            }
        }
        setShowLinkInput(false);
        setLinkUrl('');
    };

    const [initialHtml] = useState(() => ensureHtml(value));

    // Initialize content once on mount
    useEffect(() => {
        if (editorRef.current && !editorRef.current.innerHTML) {
            const html = ensureHtml(value);
            editorRef.current.innerHTML = html;
            localValueRef.current = html;
        }
    }, []);

    // Sync external changes safely
    useEffect(() => {
        if (value === localValueRef.current) {
            return;
        }
        const parsed = ensureHtml(value);
        if (editorRef.current) {
            const cleanCurrent = editorRef.current.innerHTML.replace(/\s+/g, ' ').trim();
            const cleanParsed = parsed.replace(/\s+/g, ' ').trim();
            if (cleanCurrent !== cleanParsed) {
                editorRef.current.innerHTML = parsed;
                localValueRef.current = value;
            }
        }
    }, [value]);

    const handleInput = () => {
        if (editorRef.current) {
            const html = editorRef.current.innerHTML;
            localValueRef.current = html;
            onChange(html);
        }
    };

    const handleCommand = (e: React.MouseEvent, command: string, argument: string = '') => {
        e.preventDefault();
        e.stopPropagation();
        if (editorRef.current) {
            editorRef.current.focus();
            document.execCommand(command, false, argument);
            handleInput();
            checkActiveFormats();
        }
    };

    const checkActiveFormats = () => {
        try {
            setIsBold(document.queryCommandState('bold'));
            setIsItalic(document.queryCommandState('italic'));
            setIsUnderline(document.queryCommandState('underline'));
            setIsBulletList(document.queryCommandState('insertUnorderedList'));
            setIsOrderedList(document.queryCommandState('insertOrderedList'));
        } catch (e) {
            // Ignore selection errors
        }
    };

    return (
        <div className={`border border-slate-200 rounded-xl overflow-hidden shadow-xs flex flex-col bg-white ${className}`}>
            <div className="bg-slate-50 border-b border-slate-200 px-3 py-1.5 flex items-center space-x-1 select-none z-10 min-h-[36px]">
                {showLinkInput ? (
                    <div className="flex items-center space-x-1.5 w-full">
                        <input
                            type="text"
                            placeholder="Link URL (e.g., https://example.com)"
                            value={linkUrl}
                            onChange={(e) => setLinkUrl(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    applyLink();
                                } else if (e.key === 'Escape') {
                                    setShowLinkInput(false);
                                    setLinkUrl('');
                                }
                            }}
                            className="bg-white border border-slate-200 rounded px-2.5 py-1 text-xs text-slate-800 outline-none focus:ring-1 focus:ring-[#0094c8] flex-1 max-w-sm"
                            autoFocus
                        />
                        <button
                            type="button"
                            onClick={applyLink}
                            className="px-2.5 py-1 bg-[#0094c8] text-white rounded text-xs hover:bg-[#0077a3] font-medium transition-colors cursor-pointer"
                        >
                            Add
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setShowLinkInput(false);
                                setLinkUrl('');
                            }}
                            className="px-2.5 py-1 bg-slate-200 text-slate-700 rounded text-xs hover:bg-slate-300 transition-colors cursor-pointer"
                        >
                            Cancel
                        </button>
                    </div>
                ) : (
                    <>
                        <button
                            type="button"
                            onMouseDown={(e) => handleCommand(e, 'bold')}
                            className={`p-1 px-2 rounded text-slate-700 text-xs hover:bg-slate-200 hover:text-[#011d3c] transition-colors focus:outline-none ${isBold ? 'bg-slate-200/90 text-[#0094c8] font-black shadow-xs' : 'font-semibold'}`}
                            title="Bold"
                        >
                            <Bold className="w-3.5 h-3.5 shrink-0" />
                        </button>
                        <button
                            type="button"
                            onMouseDown={(e) => handleCommand(e, 'italic')}
                            className={`p-1 px-2 rounded text-slate-700 text-xs hover:bg-slate-200 hover:text-[#011d3c] transition-colors focus:outline-none ${isItalic ? 'bg-slate-200/90 text-[#0094c8] italic font-semibold shadow-xs' : 'italic'}`}
                            title="Italic"
                        >
                            <Italic className="w-3.5 h-3.5 shrink-0" />
                        </button>
                        <button
                            type="button"
                            onMouseDown={(e) => handleCommand(e, 'underline')}
                            className={`p-1 px-2 rounded text-slate-700 text-xs hover:bg-slate-200 hover:text-[#011d3c] transition-colors focus:outline-none ${isUnderline ? 'bg-slate-200/90 text-[#0094c8] underline shadow-xs' : 'underline'}`}
                            title="Underline"
                        >
                            <Underline className="w-3.5 h-3.5 shrink-0" />
                        </button>

                        <div className="w-[1px] h-3.5 bg-slate-300 mx-1" />

                        <button
                            type="button"
                            onMouseDown={(e) => handleCommand(e, 'insertUnorderedList')}
                            className={`p-1 px-2 rounded text-slate-700 hover:bg-slate-200 hover:text-[#011d3c] transition-colors focus:outline-none ${isBulletList ? 'bg-slate-200/90 text-[#0094c8] shadow-xs' : ''}`}
                            title="Bullet List"
                        >
                            <List className="w-3.5 h-3.5 shrink-0" />
                        </button>
                        <button
                            type="button"
                            onMouseDown={(e) => handleCommand(e, 'insertOrderedList')}
                            className={`p-1 px-2 rounded text-slate-700 hover:bg-slate-200 hover:text-[#011d3c] transition-colors focus:outline-none ${isOrderedList ? 'bg-slate-200/90 text-[#0094c8] shadow-xs' : ''}`}
                            title="Numbered List"
                        >
                            <ListOrdered className="w-3.5 h-3.5 shrink-0" />
                        </button>

                        <div className="w-[1px] h-3.5 bg-slate-300 mx-1" />

                        <button
                            type="button"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                saveSelection();
                                setShowLinkInput(true);
                            }}
                            className="p-1 px-2 rounded text-slate-700 hover:bg-slate-200 hover:text-[#011d3c] transition-colors focus:outline-none"
                            title="Insert Link"
                        >
                            <Link className="w-3.5 h-3.5 shrink-0 text-[#0094c8]" />
                        </button>
                    </>
                )}
            </div>

            <div
                ref={editorRef}
                contentEditable
                className={`w-full ${minHeight} ${maxHeight} px-3.5 py-2.5 bg-[#f8fafc] text-slate-900 border-0 focus:ring-0 outline-none font-sans text-xs md:text-sm leading-relaxed tracking-normal overflow-y-auto rich-editor-content [&_p]:mb-1.5 [&_p:last-child]:mb-0 [&_ul]:my-1 [&_ul]:pl-5 [&_ul]:list-disc [&_ol]:my-1 [&_ol]:pl-5 [&_ol]:list-decimal [&_li]:mb-0.5 [&_strong]:font-bold [&_u]:underline`}
                onInput={handleInput}
                onKeyUp={checkActiveFormats}
                onMouseUp={checkActiveFormats}
                placeholder={placeholder}
            />
        </div>
    );
};
